import { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Code,
  Link,
  Badge,
  Icon,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  useColorModeValue,
  Tooltip,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react'
import {
  Shield,
  Zap,
  Terminal,
  Copy,
  FileText,
  Bot,
  BookOpen,
  Link2,
  CheckCircle,
  Lock,
  FolderOpen,
} from 'lucide-react'

// Section IDs for URL tracking
const SECTION_IDS = [
  'what-is-myrkup',
  'getting-started',
  'mcp-tools',
  'organization',
  'encryption',
]

function CodeBlock({ children, title }: { children: string; title?: string }) {
  const bg = useColorModeValue('stone.100', 'stone.800')
  const headerBg = useColorModeValue('stone.200', 'stone.700')
  const headerColor = useColorModeValue('stone.600', 'stone.300')
  const codeColor = useColorModeValue('stone.700', 'green.300')
  const copyColor = useColorModeValue('stone.500', 'stone.400')
  const copyHoverColor = useColorModeValue('stone.700', 'white')

  return (
    <Box borderRadius="lg" overflow="hidden" my={4} border="1px solid" borderColor={useColorModeValue('stone.200', 'transparent')}>
      {title && (
        <HStack
          bg={headerBg}
          px={4}
          py={2}
          justify="space-between"
        >
          <Text fontSize="sm" color={headerColor} fontFamily="mono">
            {title}
          </Text>
          <Icon
            as={Copy}
            boxSize={4}
            color={copyColor}
            cursor="pointer"
            _hover={{ color: copyHoverColor }}
            onClick={() => navigator.clipboard.writeText(children)}
          />
        </HStack>
      )}
      <Box
        bg={bg}
        p={4}
        overflowX="auto"
      >
        <Code
          display="block"
          whiteSpace="pre"
          bg="transparent"
          color={codeColor}
          fontSize="sm"
          fontFamily="mono"
        >
          {children}
        </Code>
      </Box>
    </Box>
  )
}

function FeatureCard({
  icon,
  title,
  description,
  to,
}: {
  icon: React.ElementType
  title: string
  description: string
  to?: string
}) {
  const bg = useColorModeValue('stone.50', 'stone.800')
  const bodyColor = useColorModeValue('stone.600', 'stone.300')

  const content = (
    <Box
      bg={bg}
      p={5}
      borderRadius="lg"
      transition="all 0.2s"
      _hover={to ? { transform: 'translateY(-2px)', borderColor: 'brand.500' } : undefined}
      border="1px solid"
      borderColor="transparent"
    >
      <HStack mb={2}>
        <Icon as={icon} boxSize={5} color="brand.500" />
        <Text fontWeight="semibold">{title}</Text>
      </HStack>
      <Text fontSize="sm" color={bodyColor}>
        {description}
      </Text>
    </Box>
  )

  if (to) {
    return (
      <Link as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
        {content}
      </Link>
    )
  }

  return content
}

function SectionHeader({
  icon,
  title,
  sectionId,
}: {
  icon: React.ElementType
  title: string
  sectionId: string
}) {
  const toast = useToast()
  const linkColor = useColorModeValue('stone.400', 'stone.500')
  const linkHoverColor = useColorModeValue('brand.500', 'brand.400')

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${sectionId}`
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top',
    })
  }

  return (
    <HStack role="group">
      <Icon as={icon} boxSize={5} color="brand.500" />
      <Heading size="md">{title}</Heading>
      <Tooltip label="Copy link to section" placement="top" hasArrow>
        <Box
          as="button"
          onClick={copyLink}
          p={1}
          borderRadius="md"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.2s"
          color={linkColor}
          _hover={{ color: linkHoverColor }}
        >
          <Icon as={Link2} boxSize={4} />
        </Box>
      </Tooltip>
    </HStack>
  )
}

export function DocsPage() {
  const location = useLocation()
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [, setActiveSection] = useState<string | null>(null)
  const isScrollingToHash = useRef(false)
  const subtleColor = useColorModeValue('stone.500', 'stone.400')
  const bodyColor = useColorModeValue('stone.600', 'stone.300')
  const tableBorderColor = useColorModeValue('stone.200', 'stone.600')

  // Handle initial hash navigation
  useEffect(() => {
    if (location.hash) {
      isScrollingToHash.current = true
      const element = document.getElementById(location.hash.slice(1))
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' })
          setTimeout(() => {
            isScrollingToHash.current = false
          }, 1000)
        }, 100)
      }
    }
  }, [])

  // Set up intersection observer for URL updates on scroll
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (isScrollingToHash.current) return

      const visibleEntries = entries.filter(entry => entry.isIntersecting)
      if (visibleEntries.length === 0) return

      visibleEntries.sort((a, b) => {
        const rectA = a.boundingClientRect
        const rectB = b.boundingClientRect
        return rectA.top - rectB.top
      })

      const topSection = visibleEntries[0]
      if (topSection) {
        const sectionId = topSection.target.id
        setActiveSection(sectionId)

        const newUrl = `${location.pathname}#${sectionId}`
        if (window.location.hash !== `#${sectionId}`) {
          window.history.replaceState(null, '', newUrl)
        }
      }
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    })

    SECTION_IDS.forEach(id => {
      const element = document.getElementById(id)
      if (element) {
        observerRef.current?.observe(element)
      }
    })

    return () => {
      observerRef.current?.disconnect()
    }
  }, [location.pathname])

  const mcpUrl = `${window.location.origin}/api/mcp`

  const cursorMcpConfig = `{
  "mcpServers": {
    "myrkup": {
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer mk_your-secret-key-here"
      }
    }
  }
}`

  return (
    <VStack spacing={8} align="stretch" maxW="4xl" mx="auto">
      {/* Hero */}
      <Box textAlign="center" py={4}>
        <Badge colorScheme="brand" mb={3} fontSize="sm">
          Documentation
        </Badge>
        <Heading as="h1" size="xl" fontFamily="heading" mb={3}>
          Get Started with myrkup
        </Heading>
        <Text color={subtleColor} fontSize="lg" maxW="2xl" mx="auto">
          Encrypted markdown sharing for AI workflows. Get up and running in under a minute.
        </Text>
      </Box>

      {/* What is myrkup */}
      <Card scrollMarginTop="72px" id="what-is-myrkup" role="group">
        <CardHeader>
          <SectionHeader icon={BookOpen} title="What is myrkup?" sectionId="what-is-myrkup" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <Text color={bodyColor}>
              myrkup is an encrypted markdown sharing platform for AI-powered workflows.
              Content is encrypted with AES-256-GCM before it leaves your browser or AI chat. The server never sees plaintext.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FeatureCard
                icon={FileText}
                title="Browser Editor"
                description="Write or paste markdown. Configure security and share with a link."
                to="/editor"
              />
              <FeatureCard
                icon={Terminal}
                title="REST API"
                description="Programmatic access to create, view, and manage shares."
                to="/docs/api"
              />
              <FeatureCard
                icon={Bot}
                title="MCP Integration"
                description="AI assistants create shares directly. Just add a URL to your config."
                to="/settings"
              />
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Getting Started */}
      <Card scrollMarginTop="72px" id="getting-started" role="group">
        <CardHeader>
          <SectionHeader icon={Zap} title="Getting Started" sectionId="getting-started" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={6} align="stretch">
            {/* Step 1 */}
            <Box>
              <HStack mb={2}>
                <Badge colorScheme="brand">Step 1</Badge>
                <Text fontWeight="medium">Get a Secret Key</Text>
              </HStack>
              <Text color={bodyColor} mb={2}>
                Open the{' '}
                <Link as={RouterLink} to="/editor?auth=signup" color="brand.500" fontWeight="medium">
                  editor
                </Link>{' '}
                and sign up. No email required. You get an anonymous secret key starting with <Code>mk_</Code>.
              </Text>
              <Alert status="warning" borderRadius="md" mt={2}>
                <AlertIcon />
                <Text fontSize="sm">
                  Your secret key is shown once. Store it in a password manager or environment variable.
                </Text>
              </Alert>
            </Box>

            {/* Step 2 */}
            <Box>
              <HStack mb={2}>
                <Badge colorScheme="brand">Step 2</Badge>
                <Text fontWeight="medium">Connect Your AI Assistant</Text>
              </HStack>
              <Text color={bodyColor} mb={2}>
                Add the myrkup MCP server to your AI assistant's config.
              </Text>

              <Tabs variant="enclosed" colorScheme="brand">
                <TabList>
                  <Tab>Cursor</Tab>
                  <Tab>Claude</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Text color={bodyColor} mb={2} fontSize="sm">
                      Add to <Code>.cursor/mcp.json</Code> in your project root:
                    </Text>
                    <CodeBlock title=".cursor/mcp.json">{cursorMcpConfig}</CodeBlock>
                  </TabPanel>
                  <TabPanel px={0}>
                    <Text color={bodyColor} mb={2} fontSize="sm">
                      For Claude Code (CLI), run:
                    </Text>
                    <CodeBlock title="Terminal">{`claude mcp add --transport http myrkup ${mcpUrl} \\
  --header "Authorization: Bearer mk_your-secret-key-here"`}</CodeBlock>
                    <Text color={bodyColor} mb={2} mt={4} fontSize="sm">
                      For Claude Desktop, use <strong>Settings → Connectors → Add custom connector</strong> and enter the URL above.
                    </Text>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>

            {/* Step 3 */}
            <Box>
              <HStack mb={2}>
                <Badge colorScheme="brand">Step 3</Badge>
                <Text fontWeight="medium">Share Your First Markdown</Text>
              </HStack>
              <Text color={bodyColor}>
                Ask your AI assistant: <em>"Share this README as markdown"</em>
              </Text>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* MCP Tools Reference */}
      <Card scrollMarginTop="72px" id="mcp-tools" role="group">
        <CardHeader>
          <SectionHeader icon={Shield} title="MCP Tools Reference" sectionId="mcp-tools" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={6} align="stretch">
            <Text color={bodyColor}>
              Available tools when connected via MCP:
            </Text>

            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th borderColor={tableBorderColor}>Tool</Th>
                    <Th borderColor={tableBorderColor}>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">share_markdown</Code></Td>
                    <Td borderColor={tableBorderColor}>Create an encrypted share and get a link</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">load_markdown</Code></Td>
                    <Td borderColor={tableBorderColor}>Load content from a share by ID or URL</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">list_markdowns</Code></Td>
                    <Td borderColor={tableBorderColor}>List your shares (filter by folder or tag)</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">load_all_markdowns</Code></Td>
                    <Td borderColor={tableBorderColor}>Load all shares matching a filter</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">update_markdown</Code></Td>
                    <Td borderColor={tableBorderColor}>Update an existing share's content</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">delete_markdown</Code></Td>
                    <Td borderColor={tableBorderColor}>Delete a share</Td>
                  </Tr>
                  <Tr>
                    <Td borderColor={tableBorderColor}><Code fontSize="xs">get_account_info</Code></Td>
                    <Td borderColor={tableBorderColor}>Check usage limits and account status</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>

            <Box>
              <Text fontWeight="medium" mb={2}>Common Parameters</Text>
              <List spacing={2}>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">filename</Code>: Name for the share (e.g., "api-design.md")
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">folder_path</Code>: Virtual folder (e.g., "/myproject/plans")
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">tags</Code>: Array of tags for filtering (e.g., ["qa", "report"])
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">expires_in</Code>: Expiration time. Free: max 7d, Starter: max 30d, Pro: up to "never"
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircle} color="brand.500" mt={1} />
                  <Text fontSize="sm" color={bodyColor}>
                    <Code fontSize="xs">burn_after_read</Code>: Self-destruct after first view (true/false)
                  </Text>
                </ListItem>
              </List>
            </Box>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Your AI assistant can decrypt your own shares without a passcode using your API key.
              </Text>
            </Alert>
          </VStack>
        </CardBody>
      </Card>

      {/* Organization */}
      <Card scrollMarginTop="72px" id="organization" role="group">
        <CardHeader>
          <SectionHeader icon={FolderOpen} title="Organization" sectionId="organization" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <Text color={bodyColor}>
              Organize shares with virtual folders and tags. These are metadata for filtering, not real directories.
            </Text>

            <Box>
              <Text fontWeight="medium" mb={2}>Folder Structure</Text>
              <Text fontSize="sm" color={bodyColor} mb={2}>
                Use folder paths to organize by project or purpose:
              </Text>
              <CodeBlock>{`/myproject/plans      # Architecture docs, implementation plans
/myproject/rules      # Cursor rules, AI context files
/myproject/qa         # Test results, QA reports
/myproject/scratch    # Temporary notes, debug logs`}</CodeBlock>
            </Box>

            <Box>
              <Text fontWeight="medium" mb={2}>Tags</Text>
              <Text fontSize="sm" color={bodyColor} mb={2}>
                Add tags for cross-project filtering:
              </Text>
              <HStack flexWrap="wrap" spacing={2}>
                {['plan', 'rules', 'qa', 'report', 'draft', 'context'].map((tag) => (
                  <Badge key={tag} colorScheme="brand" variant="subtle" fontSize="xs" px={2} py={0.5}>
                    {tag}
                  </Badge>
                ))}
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="medium" mb={2}>Example Prompts</Text>
              <VStack spacing={2} align="stretch">
                <Code fontSize="sm" p={2} borderRadius="md">"List my shares in /myproject/plans"</Code>
                <Code fontSize="sm" p={2} borderRadius="md">"Load all shares tagged 'rules'"</Code>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* How Encryption Works */}
      <Card scrollMarginTop="72px" id="encryption" role="group">
        <CardHeader>
          <SectionHeader icon={Lock} title="How Encryption Works" sectionId="encryption" />
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <Text color={bodyColor}>
              Every share is encrypted with AES-256-GCM. The encryption key is derived from a passcode that only you and link recipients know.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontWeight="medium" mb={2}>Browser Shares</Text>
                <List spacing={1}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>Encrypted in your browser before upload</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>Key derived with Argon2id (GPU-resistant)</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>Server only stores ciphertext</Text>
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Text fontWeight="medium" mb={2}>MCP/API Shares</Text>
                <List spacing={1}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>Encrypted on server with PBKDF2</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>Dual-key wrapping (passcode + API key)</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircle} color="brand.500" mt={1} boxSize={4} />
                    <Text fontSize="sm" color={bodyColor}>You can decrypt your own shares via API</Text>
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Box bg={useColorModeValue('green.50', 'green.900')} p={3} borderRadius="md" textAlign="center">
                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('green.700', 'green.200')}>Zero-knowledge</Text>
              </Box>
              <Box bg={useColorModeValue('green.50', 'green.900')} p={3} borderRadius="md" textAlign="center">
                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('green.700', 'green.200')}>No account to view</Text>
              </Box>
              <Box bg={useColorModeValue('green.50', 'green.900')} p={3} borderRadius="md" textAlign="center">
                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('green.700', 'green.200')}>Burn after read</Text>
              </Box>
              <Box bg={useColorModeValue('green.50', 'green.900')} p={3} borderRadius="md" textAlign="center">
                <Text fontSize="xs" fontWeight="medium" color={useColorModeValue('green.700', 'green.200')}>Auto-expiration</Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
